import { useRef } from 'react';

/** Arrow keys move by one and wrap, per the WAI-ARIA radiogroup pattern. */
const KEY_DELTA: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/**
 * The keyboard and focus machinery shared by every single-choice group here.
 *
 * `SegmentedControl` and `TileRadioGroup` had this written out twice,
 * character-for-character — the same `KEY_DELTA` map, the same `select`, the
 * same `handleKeyDown` — differing only in the name of the ref array. Their
 * docstrings described that as "deliberately kept in step", which is the
 * sentence a pair of copies is always given right up until they drift.
 *
 * ## What the caller still owns
 *
 * Only the markup. The hook hands back a ref array to attach to the option
 * elements, the index that may hold `tabIndex={0}`, and the two handlers.
 *
 * ## Why exactly one element is tabbable
 *
 * A roving tabindex. Without it, Tab walks through every option in the group —
 * ten stops to cross one question — which is precisely what the ARIA pattern
 * exists to prevent. With nothing selected the first option takes the stop,
 * otherwise Tab would skip the group entirely and the user could never reach it.
 *
 * ## Why selection follows focus
 *
 * The APG default for a radiogroup whose selection has no expensive side
 * effect. Both call sites only set local state, so moving focus and moving the
 * selection are the same gesture, and separating them would force the user to
 * press Space after every arrow.
 */
export function useRovingRadioGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  /** `readonly` because callers build their option lists with `as const`. */
  options: readonly { value: T }[];
  value: T | undefined;
  onChange: (value: T) => void;
}) {
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = options.findIndex(o => o.value === value);
  const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const select = (index: number) => {
    onChange(options[index].value);
    items.current[index]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const delta = KEY_DELTA[event.key];
    let next: number | undefined;
    if (delta !== undefined) {
      next = (index + delta + options.length) % options.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = options.length - 1;
    }
    if (next === undefined) return;
    // Otherwise Arrow/Home/End would also scroll whatever sits underneath.
    event.preventDefault();
    select(next);
  };

  return { items, selectedIndex, tabbableIndex, select, handleKeyDown };
}
